import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CatalogViewServiceBase } from '@app/super/catalog-view-service-base';
import { AutodeskViewerDto, createFileInvalidAutodeskViewerDto } from '@commonInterfaces/AutodeskViewerModel';
import { RequestService } from "@commonServices/request.service";
import { Subject, firstValueFrom, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CadenasDto, CatalogAboutUsCategory, CatalogItemDetailsDto, CatalogItemFolderViewDto, CatalogItemMetaDto, Client as CatalogViewAPI, CatalogViewAboutUsDto, CatalogViewCatalogInformationDto, CatalogViewContactListDto, CatalogViewForeignLanguageCatalogsDto, CatalogViewGroupDto, CatalogViewNewsListDto, CatalogViewNewsShortDto, CatalogViewRelatedCatalogsDto, CatalogViewTagDto, DgnbDto, SentinelDto, ViewGroupItemDto } from "@interfaces/HttpClient/CatalogApiCatalogViewModels";

@Injectable()
export class CatalogViewService extends CatalogViewServiceBase {

	constructor(
		private readonly catalogViewApi: CatalogViewAPI,
		private requestService: RequestService
	) { super() }
	private nextViewerCall: Subject<void> = new Subject<void>();
	private nextMetaCall: Subject<void> = new Subject<void>();
	private static newsInit: { key: string; value: CatalogViewNewsShortDto[] };
	private static news: { key: string; value: CatalogViewNewsListDto };
	private static newsCatalogKey: string;
	private static catalogNewsCache: CatalogViewNewsListDto;
	private static currentCatalogMeta: { key: string, value: CatalogViewGroupDto };
	private static currentCatalogInfo: { key: string; value: CatalogViewCatalogInformationDto };
	private static currentForeignLanguageCatalogs: { key: string; value: CatalogViewForeignLanguageCatalogsDto[] };
	private static currentRelatedCatalogs: { key: string; value: CatalogViewRelatedCatalogsDto[] };
	private static currentTags: { key: string; value: CatalogViewRelatedCatalogsDto[] };
	private static currentAttachment: { guid: string; value: CatalogItemDetailsDto };
	private static currentContact: { key: string; value: CatalogViewContactListDto };
	private static currentCatalogItemMeta: { guid: string; value: CatalogItemMetaDto };
	private static currentCadenasData: { url: string; value: CadenasDto };
	private static currentAboutUsData: { key: string; value: CatalogViewAboutUsDto };
	private static currentSustainabilityData: { key: string; value: CatalogViewAboutUsDto };
	private static currentReferencesData: { key: string; value: CatalogViewAboutUsDto };
	private static currentServiceData: { key: string; value: CatalogViewAboutUsDto };

	private static attachments: ViewGroupItemDto[];

	setAttachments(attachments: ViewGroupItemDto[]) {
		CatalogViewService.attachments = attachments;
	}

	getAttachments() {
		return CatalogViewService.attachments;
	}

	async getDgnbSustainabilityData(catalogItemGuid: string): Promise<DgnbDto> {
		try {
			return firstValueFrom(this.catalogViewApi.catalogView_GetDgnbSustainabilityInfo(catalogItemGuid))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getSentinelSustainabilityData(catalogItemGuid: string): Promise<SentinelDto> {
		try {
			return firstValueFrom(this.catalogViewApi.catalogView_GetSentinelSustainabilityInfo(catalogItemGuid))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getRelatedCatalogs(key: string): Promise<CatalogViewRelatedCatalogsDto[]> {
		try {
			if (CatalogViewService.currentRelatedCatalogs?.key === key) {
				return firstValueFrom(of(CatalogViewService.currentRelatedCatalogs?.value))
			}
			const preloaded = await this.consumeSeoPreload<CatalogViewRelatedCatalogsDto[]>('relatedCatalogs', 'key', key);
			if (preloaded) {
				CatalogViewService.currentRelatedCatalogs = { key, value: preloaded };
				return preloaded;
			}
			const result = await firstValueFrom(this.catalogViewApi
				.catalogView_GetRelatedCatalogsByCatalogKey(key));
			CatalogViewService.currentRelatedCatalogs = {
				key, value: result
			};
			return result;

		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getForeignLanguageCatalogs(key: string): Promise<CatalogViewForeignLanguageCatalogsDto[]> {
		try {
			if (CatalogViewService.currentForeignLanguageCatalogs?.key === key) {
				return firstValueFrom(of(CatalogViewService.currentForeignLanguageCatalogs?.value))
			}
			const preloaded = await this.consumeSeoPreload<CatalogViewForeignLanguageCatalogsDto[]>('foreignLanguageCatalogs', 'key', key);
			if (preloaded) {
				CatalogViewService.currentForeignLanguageCatalogs = { key, value: preloaded };
				return preloaded;
			}
			const result = await firstValueFrom(this.catalogViewApi
				.catalogView_GetForeignLanguageCatalogsByCatalogKey(key));
			CatalogViewService.currentForeignLanguageCatalogs = {
				key, value: result
			};
			return result;
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getAttachmentTags(key: string): Promise<CatalogViewTagDto[]> {
		try {
			if (CatalogViewService.currentTags?.key === key) {
				return firstValueFrom(of(CatalogViewService.currentTags?.value))
			}
			const result = await firstValueFrom(this.catalogViewApi
				.catalogView_GetAttachmentTagsByCatalogKey(key));
			CatalogViewService.currentTags = {
				key, value: result
			};
			return result;
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCatalogInformation(
		key: string
	): Promise<CatalogViewCatalogInformationDto> {
		try {
			this.nextViewerCall.next();
			if (CatalogViewService.currentCatalogInfo?.key === key) {
				return firstValueFrom(of(CatalogViewService.currentCatalogInfo?.value))
			}

			const preloadedInfo = await this.consumeSeoPreload<CatalogViewCatalogInformationDto>('catalogInfo', 'key', key);
			if (preloadedInfo) {
				CatalogViewService.currentCatalogInfo = { key, value: preloadedInfo };
				return preloadedInfo;
			}

			const result = await firstValueFrom(this.catalogViewApi
				.catalogView_GetCatalogInformationByCatalogKey(key)
				.pipe(takeUntil(this.nextViewerCall)));
			CatalogViewService.currentCatalogInfo = {
				key, value: result
			};
			return result;

		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getNewsForCatalogView(key: string): Promise<CatalogViewNewsShortDto[]> {
		try {
			if (key === CatalogViewService.news?.key) {
				return firstValueFrom(of(CatalogViewService.newsInit?.value))
			}
			const result = await firstValueFrom(this.catalogViewApi
				.catalogView_GetNewsForCatalogView(key));
			CatalogViewService.newsInit = {
				key, value: result
			}
			return result;
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCatalogNewsForDialog(catalogKey: string): Promise<CatalogViewNewsListDto> {
		try {
			if (catalogKey === CatalogViewService.newsCatalogKey) {
				return firstValueFrom(of(CatalogViewService.catalogNewsCache))
			}
			const result = await firstValueFrom(this.catalogViewApi
				.catalogView_GetNewsForDialog(catalogKey));
			CatalogViewService.newsCatalogKey = catalogKey;
			CatalogViewService.catalogNewsCache = result;
			return result;
		} catch (error) {
			return this.requestService.errorHandling(error, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	// catalog content // position from catalog tree
	async getAttachmentsByCatalogItemGuid(
		guid: string
	): Promise<CatalogItemDetailsDto> {
		try {
			this.nextViewerCall.next();
			if (guid === CatalogViewService.currentAttachment?.guid) {
				return firstValueFrom(of(CatalogViewService.currentAttachment?.value))
			}

			const preloadedDetails = await this.consumeSeoPreload<CatalogItemDetailsDto>('catalogDetails', 'guid', guid);
			if (preloadedDetails) {
				CatalogViewService.currentAttachment = { guid, value: preloadedDetails };
				return preloadedDetails;
			}

			// GetAttachments / Flache Liste
			const result = await firstValueFrom(this.catalogViewApi
				.catalogView_GetCatalogItemDetailsByCatalogItemGuid(guid)
				.pipe(takeUntil(this.nextViewerCall)));
			CatalogViewService.currentAttachment = {
				guid, value: result
			}
			return result;

		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	// catalog content // katalog info ... company info ... related catalogs
	async getCatalogMeta(key: string): Promise<CatalogViewGroupDto> {
		try {
			this.nextViewerCall.next();
			if (CatalogViewService.currentCatalogMeta?.key === key) {
				return firstValueFrom(of(CatalogViewService.currentCatalogMeta?.value))
			}

			const preloadedMeta = await this.consumeSeoPreload<CatalogViewGroupDto>('catalogMeta', 'key', key);
			if (preloadedMeta) {
				CatalogViewService.currentCatalogMeta = { key, value: preloadedMeta };
				return preloadedMeta;
			}

			const result = await firstValueFrom(this.catalogViewApi.catalogView_GetCatalogMetaByCatalogKey(key).pipe(takeUntil(this.nextViewerCall)));
			CatalogViewService.currentCatalogMeta = {
				key, value: result
			};
			return result;
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getFolderView(catalogGuids: string[]): Promise<CatalogItemFolderViewDto[]> {
		try {
			this.nextViewerCall.next();
			return await firstValueFrom(this.catalogViewApi.catalogView_GetFolderView(catalogGuids).pipe(takeUntil(this.nextViewerCall)));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	public callNextView() {
		this.nextViewerCall.next();
	}

	async getContactPersonsView(key: string): Promise<CatalogViewContactListDto> {
		try {
			this.nextViewerCall.next();
			if (CatalogViewService.currentContact?.key === key) {
				return firstValueFrom(of(CatalogViewService.currentContact?.value))
			}
			const result = await firstValueFrom(this.catalogViewApi.catalogView_GetCatalogContactsByCatalogKey(key).pipe(takeUntil(this.nextViewerCall)));
			CatalogViewService.currentContact = {
				key, value: result
			};
			return result;
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	private async getAboutUsView(key?: string): Promise<CatalogViewAboutUsDto> {
		try {
			this.nextViewerCall.next();
			if (CatalogViewService.currentAboutUsData?.key === key) {
				return firstValueFrom(of(CatalogViewService.currentAboutUsData?.value))
			}
			const result = await firstValueFrom(this.catalogViewApi.catalogView_GetCatalogAboutUsCategoryInfoByCatalogKey(key, CatalogAboutUsCategory.AboutUs).pipe(takeUntil(this.nextViewerCall)));
			CatalogViewService.currentAboutUsData = {
				key, value: result
			}
			return result;
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	private async getSustainabilityView(key?: string): Promise<CatalogViewAboutUsDto> {
		try {
			this.nextViewerCall.next();
			if (CatalogViewService.currentSustainabilityData?.key === key) {
				return firstValueFrom(of(CatalogViewService.currentSustainabilityData?.value))
			}
			const result = await firstValueFrom(this.catalogViewApi.catalogView_GetCatalogAboutUsCategoryInfoByCatalogKey(key, CatalogAboutUsCategory.Sustainability).pipe(takeUntil(this.nextViewerCall)));
			CatalogViewService.currentSustainabilityData = {
				key, value: result
			}
			return result;
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	private async getReferencesView(key?: string): Promise<CatalogViewAboutUsDto> {
		try {
			this.nextViewerCall.next();
			if (CatalogViewService.currentReferencesData?.key === key) {
				return firstValueFrom(of(CatalogViewService.currentReferencesData?.value))
			}
			const result = await firstValueFrom(this.catalogViewApi.catalogView_GetCatalogAboutUsCategoryInfoByCatalogKey(key, CatalogAboutUsCategory.References).pipe(takeUntil(this.nextViewerCall)));
			CatalogViewService.currentReferencesData = {
				key, value: result
			}
			return result;
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getServiceView(key?: string): Promise<CatalogViewAboutUsDto> {
		try {
			this.nextViewerCall.next();
			if (CatalogViewService.currentServiceData?.key === key) {
				return firstValueFrom(of(CatalogViewService.currentServiceData?.value))
			}
			const result = await firstValueFrom(this.catalogViewApi.catalogView_GetCatalogAboutUsCategoryInfoByCatalogKey(key, CatalogAboutUsCategory.ServiceAdviceTools).pipe(takeUntil(this.nextViewerCall)));
			CatalogViewService.currentServiceData = {
				key, value: result
			}
			return result;
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getAboutUsCategoryView(key?: string, category?: CatalogAboutUsCategory): Promise<CatalogViewAboutUsDto> {
		try {
			switch (category) {
				case CatalogAboutUsCategory.AboutUs:
					return await this.getAboutUsView(key);
				case CatalogAboutUsCategory.Sustainability:
					return await this.getSustainabilityView(key);
				case CatalogAboutUsCategory.References:
					return await this.getReferencesView(key);
				case CatalogAboutUsCategory.ServiceAdviceTools:
					return await this.getServiceView(key);
				case CatalogAboutUsCategory.None:
					throw new Error("InvalidCategoryError");
			}
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getCatalogItemMeta(guid: string): Promise<CatalogItemMetaDto> {
		try {
			this.nextMetaCall.next();
			if (CatalogViewService.currentCatalogItemMeta?.guid === guid) {
				return firstValueFrom(of(CatalogViewService.currentCatalogItemMeta?.value))
			}

			const preloadedItemMeta = await this.consumeSeoPreload<CatalogItemMetaDto>('catalogItemMeta', 'guid', guid);
			if (preloadedItemMeta) {
				CatalogViewService.currentCatalogItemMeta = { guid, value: preloadedItemMeta };
				return preloadedItemMeta;
			}

			const result = await firstValueFrom(this.catalogViewApi.catalogView_GetCatalogItemMetaDataByGuid(guid).pipe(takeUntil(this.nextMetaCall)));
			CatalogViewService.currentCatalogItemMeta = {
				guid, value: result
			};
			return result;
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getCadenasData(cadenasUrl: string): Promise<CadenasDto> {
		try {
			this.nextMetaCall.next();
			if (CatalogViewService.currentCadenasData?.url === cadenasUrl) {
				return firstValueFrom(of(CatalogViewService.currentCadenasData?.value))
			}
			const result = await firstValueFrom(this.catalogViewApi.catalogView_GetCadenasDataByUrl(cadenasUrl).pipe(takeUntil(this.nextMetaCall)));
			CatalogViewService.currentCadenasData = {
				url: cadenasUrl, value: result
			};
			return result;
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getAutodeskViewerSource(attachmentId: number): Promise<AutodeskViewerDto> {
		try {
			return await firstValueFrom(this.catalogViewApi.catalogView_GetAutodeskViewerSource(attachmentId));
		} catch (error) {
			return this.requestService.errorHandling(error, null, [
				{ status: HttpStatusCode.NoContent, return: undefined },
				{ status: HttpStatusCode.Conflict, return: createFileInvalidAutodeskViewerDto }
			]);
		}
	}

	private async consumeSeoPreload<T>(preloadKey: string, matchProp: 'key' | 'guid', matchValue: string): Promise<T | null> {
		const seoPreload = (window as any).__seoPreload;
		if (seoPreload?.[preloadKey]?.[matchProp] === matchValue) {
			const preloaded = await seoPreload[preloadKey].data;
			delete seoPreload[preloadKey];
			return preloaded ?? null;
		}
		return null;
	}
}
